import React, { useMemo } from 'react';
import { useLanguage } from 'Context/LanguageContext';
import { StatusColor } from '@core/constants/task/colorCodes.constants';
import Tags from '~/Tags/Tags.component';
import TaskStatus from '~/Status/TaskStatus';
import Priority from '@core/constants/task/TaskPriority.constants';
import { Box, Button, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { CottonCandy, DeepSpaceGrey } from '@core/theme/baseTheme';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TASK_STATUS from '@core/constants/task/TaskStatus.constants';
import { useSelector } from 'react-redux';
import { selectUser } from '@core/store/modules/session/sessionSelectors';
import SmartTaskDetailIcons from './SmartTaskDetailIcons';
import SmartTaskFunctionalities from './SmartTaskFunctionalities';
import { buildTaskDetailIcons, buildFunctionalityIcons } from './SmartTaskIconsUtils';
import StarIcon from '@mui/icons-material/Star';
import Tooltip from '@mui/material/Tooltip';
import parsePhoneNumberFromString from 'libphonenumber-js';

const renderPhoneWithCountryCode = (phone) => {
	if (!phone) return '';
	const parsed = parsePhoneNumberFromString(phone);
	if (!parsed?.isValid()) return phone;
	const countryCode = parsed.country;
	const formatted = parsed.formatInternational();

	return `${countryCode} ${formatted}`;
};
const MAX_LENGTH = 30;
const renderEllipsisContent = ({ value, href }) => {
	const shouldTruncate = value.length > MAX_LENGTH;
	const shortText = shouldTruncate ? `${value.slice(0, MAX_LENGTH)}...` : value;

	const content = (
		<Box
			sx={{
				maxWidth: '100%',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
			}}
		>
			<Typography
				component="span"
				sx={{
					display: 'inline-block',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
					fontSize: '0.75rem',
				}}
			>
				{shortText}
			</Typography>
		</Box>
	);

	const wrappedContent = href ? (
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			style={{
				textDecoration: 'none',
				color: 'inherit',
				display: 'block',
				maxWidth: '100%',
			}}
		>
			{content}
		</a>
	) : content;

	return shouldTruncate ? (
		<Tooltip title={value} arrow>
			{wrappedContent}
		</Tooltip>
	) : wrappedContent;
};
const EllipsisAddressLinkCellComponent = ({ value }) => {
	if (!value) return null;
	const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}`;
	return renderEllipsisContent({ value, href: mapsUrl });
};
const EllipsisTextCellComponent = ({ value }) => {
	if (!value) return null;
	return renderEllipsisContent({ value });
};
const PhoneCellComponent = ({ value }) => {
	const formatted = renderPhoneWithCountryCode(value);
	return (
		<Tooltip title={`${value}`}>
			{formatted}
			{/*<a href={`tel:${value}`} style={{ textDecoration: 'none', color: 'inherit' }}>
				{formatted}
			</a>*/}
		</Tooltip>
	);
};
const StarRatingCellComponent = ({ value, row }) => {
	const comment = row?.ratingClientCommentTask;
	return (
		<Tooltip title={comment || ''} arrow>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
				{[1, 2, 3, 4, 5].map((i) => {
					const fillPercent = i <= value ? 100 : i - 1 < value ? (value % 1) * 100 : 0;
					return (
						<Box key={i} sx={{ position: 'relative', width: 20, height: 20, display: 'inline-block', mr: 0.2 }}>
							<StarIcon sx={{ color: '#ccc', position: 'absolute', top: 0, left: 0, fontSize: '18px' }} />
							<Box sx={{ width: `${fillPercent}%`, overflow: 'hidden', position: 'absolute', top: 0, left: 0, }}>
								<StarIcon sx={{ color: '#FFD700', fontSize: '18px' }} />
							</Box>
						</Box>
					);
				})}
			</Box>
		</Tooltip>
	);
}
const TaskIconCellComponent = ({ value }) => {
	const lan = useLanguage();
	const taskIcons = React.useMemo(() => buildTaskDetailIcons(value, lan), [value, lan]);
	return <SmartTaskDetailIcons taskIcons={taskIcons} icons={value} />;
};
const TaskTagsCellComponent = ({ value }) => (
	<Grid container alignContent='center'><Tags tags={value} /></Grid>
);
const DateCellComponent = ({ value, row, idKey }) => {
	const lan = useLanguage();
	const language = lan._language.replace('_', '-');
	const localDate = useMemo(() => {
		if (!value) return '';
		const tz = row.location?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
		const date = new Date(value);
		try {
			return date.toLocaleString(language, { timeZone: tz });
		} catch (error) {
			console.error(error);
			return date.toLocaleString(language, { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });
		}
	}, [value, row.location?.timeZone, language]);
	return <span>{localDate}</span>;
};
const isOwner = (user, task) => user.idAccount === task.accountId;
const isLastOwner = (user, task) => {
	if (task.partnerTaskRelations.length) {
		const lastElement = task.partnerTaskRelations.length - 1;
		const lastRelation = task.partnerTaskRelations[lastElement];
		return lastRelation.idAccountOwner === user.idAccount;
	}
	return false;
};
const StatusCellComponent = ({ value: status, row: task }) => {
	const user = useSelector(selectUser);
	const text = useMemo(() => {
		if (status === TASK_STATUS.ASSIGNED_TO_PARTNER && !isOwner(user, task) && !isLastOwner(user, task))
			return 'taskStatus_8_1';
		return `taskStatus_${status}`;
	}, [status, task, user]);
	const statusInfo = { text, ...StatusColor[status] };
	return <TaskStatus status={statusInfo} />;
};
const InventoryStatusCellComponent = ({ value: status }) => {
	const lan = useLanguage();
	const statusText = status ? status.status : lan.noStatus;
	return (
		<div className='col-sm-10 mx-auto p-0' key={statusText}>
			<div className="row justify-content-center">
				{status && (
					<div className="col-sm-1 pt-1">
						<div
							className="SmartTable-circle-texture text-center my-auto "
							style={{ backgroundColor: status.color }}
						/>
					</div>
				)}
				<div className="my-auto col-auto pl-2 pt-1">
					{statusText}
				</div>
			</div>
		</div>
	);
};
const TaskTypeCellComponent = ({ value }) => {
	const lan = useLanguage();
	return useMemo(() => {
		switch (value) {
			case 'Volume':
				return lan.volume;
			case 'Pickup':
				return lan.pickup;
			case 'Workforce':
				return lan.workforce;
			case 'Delivery':
				return lan.delivery;
			case 'Group Task':
				return lan.grouptask;
			case 'Remote':
				return lan.remote;
			default:
				return value;
		}
	}, [value, lan]);
};
const ParentTaskTypeCellComponent = ({ value }) => {
	const lan = useLanguage();
	return useMemo(() => {
		const mappings = {
			Volume: lan.volume,
			Pickup: lan.pickup,
			Workforce: lan.workforce,
			Delivery: lan.delivery,
			'Group Task': lan.grouptask,
			Remote: lan.remote,
		};
		let result = value;
		Object.keys(mappings).forEach((key) => {
			result = result.replace(new RegExp(`\\b${key}\\b`, 'gi'), mappings[key]);
		});
		return result;
	}, [value, lan]);
};
const PriorityCellComponent = ({ value: priority, row }) => {
	const lan = useLanguage();
	return useMemo(() => {
		switch (priority) {
			case Priority.HIGH:
				return lan.high;
			case Priority.MEDIUM:
				return lan.medium;
			case Priority.LOW:
				return lan.low;
			default:
				return row.priorityName;
		}
	}, [priority, row.priorityName, lan]);
};
const AgentCellComponent = ({ value: agent }) => {
	return useMemo(() => {
		if (agent && agent.name) return `${agent.name} ${agent.lastName}`;
		return '';
	}, [agent]);
};
const ParentTaskCellComponent = ({ value: parentTask }) => {
	return useMemo(() => (parentTask ? parentTask : ''), [parentTask]);
};
const TaskFunctionalitiesCellComponent = ({ value }) => {
	const lan = useLanguage();
	const funcProps = useMemo(() => {
		if (!value) return null;
		const { forms, assets, inventory } = value;
		return { forms, assets, inventory };
	}, [value]);
	if (!funcProps) return '';
	const icons = React.useMemo(() => buildFunctionalityIcons(funcProps, lan), [funcProps, lan]);
	return <SmartTaskFunctionalities {...funcProps} functionalityIcons={icons} />;
};
const TaskDetailButtonComponent = ({ value: taskId, onClick }) => {
	return (
		<Button
			className="fielder-task-id-button"
			onClick={onClick}
			sx={{ background: CottonCandy, color: DeepSpaceGrey, borderRadius: '12px', px: 2 }}
			startIcon={<VisibilityIcon />}
		>
			{taskId}
		</Button>
	);
};
export const EllipsisAddressLinkCell = React.memo(EllipsisAddressLinkCellComponent);
export const EllipsisTextCell = React.memo(EllipsisTextCellComponent);
export const PhoneCell = React.memo(PhoneCellComponent);
export const StarRatingCell = React.memo(StarRatingCellComponent);
export const TaskIconCell = React.memo(TaskIconCellComponent);
export const TaskTagsCell = React.memo(TaskTagsCellComponent);
export const DateCell = React.memo(DateCellComponent);
export const StatusCell = React.memo(StatusCellComponent);
export const TaskTypeCell = React.memo(TaskTypeCellComponent);
export const ParentTaskTypeCell = React.memo(ParentTaskTypeCellComponent);
export const PriorityCell = React.memo(PriorityCellComponent);
export const AgentCell = React.memo(AgentCellComponent);
export const ParentTaskCell = React.memo(ParentTaskCellComponent);
export const TaskFunctionalitiesCell = React.memo(TaskFunctionalitiesCellComponent);
export const TaskDetailButton = React.memo(TaskDetailButtonComponent);
export const InventoryStatusCell = React.memo(InventoryStatusCellComponent);
